// Bulk-upload template for the Situation Report sheet. Builds a
// downloadable CSV whose columns match statistics_acp / the backend's
// ACP_ENTRY_FIELDS. The upload endpoint (/common/acp-bulk-upload/) reads
// back suco_code + date + any of the impact fields; adm1/adm2/adm3 are
// reference-only. Keep IMPACT_COLUMNS in sync with DailySituationReport.js.

export const CORE_COLUMNS = [
    { key: "suco_code", label: "Suco Code", note: "Exact adm3_code from the master Suco list (e.g. TL0101) - read back on import", sample: "TL0101" },
    { key: "adm1", label: "Municipality", note: "Reference only - filled in for you, ignored on import", sample: "" },
    { key: "adm2", label: "Administrative Post", note: "Reference only - filled in for you, ignored on import", sample: "" },
    { key: "adm3", label: "Suco", note: "Reference only - filled in for you, ignored on import", sample: "" },
    { key: "date", label: "Date", note: "Date this row is for, format YYYY-MM-DD - read back on import", sample: "" },
];

// Same field set/order as IMPACT_COLUMNS in DailySituationReport.js.
const NUM = "Whole number, leave blank if none/unknown";
const TXT = "Free text, leave blank if none";
export const IMPACT_COLUMNS = [
    { key: "location", label: "Location", note: TXT },
    { key: "wind_damage", label: "Wind Damage", note: NUM },
    { key: "fire", label: "Fire", note: NUM },
    { key: "flood", label: "Flood", note: NUM },
    { key: "landslide", label: "Landslide", note: NUM },
    { key: "storm_surge", label: "Storm Surge", note: NUM },
    { key: "social_conflict", label: "Social Conflict", note: NUM },
    { key: "other_hazard", label: "Other Hazard", note: NUM },
    { key: "road_accident", label: "Road Accident", note: NUM },
    { key: "dead", label: "Dead", note: NUM },
    { key: "injured", label: "Injured", note: NUM },
    { key: "missing", label: "Missing", note: NUM },
    { key: "affected_families", label: "Affected Families", note: NUM },
    { key: "total_family_members", label: "Total Family Members", note: NUM },
    { key: "evacuated_people", label: "Evacuated People", note: NUM },
    { key: "damaged_road", label: "Damaged Road", note: NUM },
    { key: "bridges", label: "Bridges", note: NUM },
    { key: "damaged_houses", label: "Damaged Houses", note: NUM },
    { key: "public_buildings", label: "Public Buildings", note: NUM },
    { key: "remarks", label: "Remarks", note: TXT },
].map((c) => ({ ...c, sample: "" }));

const csvEscape = (value) => {
    const str = String(value ?? "");
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const toCsvRow = (values) => values.map(csvEscape).join(",");

// Header row, a "#INSTRUCTIONS" row explaining each column, then one row
// per suco with suco_code/adm1/adm2/adm3/date pre-filled. `date` is
// whatever was picked in the Date field, always YYYY-MM-DD, stamped onto
// every row (the import reads the date per row, so a filled file can even
// span multiple days if edited that way).
export const buildDsrTemplateCsv = (sucoRows, date) => {
    const columns = [...CORE_COLUMNS, ...IMPACT_COLUMNS];
    const header = toCsvRow(columns.map((c) => c.key));
    const instructions = toCsvRow(["#INSTRUCTIONS", ...columns.slice(1).map((c) => c.note)]);

    const rowValues = (suco) => columns.map((c) => (
        c.key === "suco_code" ? (suco ? suco.adm3_code : "")
            : c.key === "adm1" ? (suco ? suco.adm1 : "")
                : c.key === "adm2" ? (suco ? suco.adm2 : "")
                    : c.key === "adm3" ? (suco ? suco.adm3 : "")
                        : c.key === "date" ? (date || "")
                            : ""
    ));

    // Sorted by suco_code so the sheet is in a predictable, findable order
    // rather than whatever order the API happened to return them in.
    const sortedSucoRows = sucoRows
        ? [...sucoRows].sort((a, b) => String(a.adm3_code).localeCompare(String(b.adm3_code)))
        : sucoRows;

    const dataRows = (sortedSucoRows && sortedSucoRows.length > 0)
        ? sortedSucoRows.map((suco) => rowValues(suco))
        : [rowValues(null)];

    return [header, instructions, ...dataRows.map(toCsvRow)].join("\r\n") + "\r\n";
};

export const downloadCsv = (filename, csvText) => {
    const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};
